import React, { useEffect, useState } from "react";
import { Button, Space, Table, Tag } from "antd";
import { useMachines } from "../../context/MachineContext";
import { useDepartments } from "../../context/DepartmentContext";
import DetailDrawer from "./DetailDrawer";
import { LineChartOutlined } from "@ant-design/icons";

const TableComponent = () => {
  const { machines } = useMachines();
  const { departments } = useDepartments();
  const [filteredData, setFilteredData] = useState([]);
  const [data, setData] = useState([]);
  const [selectedMachineId, setSelectedMachineId] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedMachine, setSelectedMachine] = useState({
    name: "",
  });

  const apiUrl = process.env.REACT_APP_MONGO_URL;

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];

    fetch(`${apiUrl}/api/dailyWorkingHours`)
      .then((res) => res.json())
      .then((data) => {
        setData(data);

        // Bugüne ait verileri filtrele
        const todayData = data.filter((item) => item.date.startsWith(today));

        // Aynı makineye ait saat ve dakikaları topla
        const summedData = todayData.reduce((acc, item) => {
          const machineId = item.machineId; // Makine ID'si

          // dailyWorkingHours'ı saat ve dakikaya ayır
          const [hours, minutes] = item.dailyWorkingHours
            .split(":")
            .map(Number); // "5:42:45.146000" -> [5, 42]

          if (!acc[machineId]) {
            acc[machineId] = { hours: 0, minutes: 0 }; // Eğer bu makine daha önce eklenmediyse, başlangıç değeri ata
          }

          // Saat ve dakikaları topla
          acc[machineId].hours += hours;
          acc[machineId].minutes += minutes;

          // Dakikalar 60'ı geçerse, saate çevir
          if (acc[machineId].minutes >= 60) {
            acc[machineId].hours += Math.floor(acc[machineId].minutes / 60);
            acc[machineId].minutes = acc[machineId].minutes % 60;
          }

          return acc;
        }, {});

        // Toplanan veriyi saat:dakika formatında state'e kaydet
        const formattedData = Object.keys(summedData).map((machineId) => {
          const { hours, minutes } = summedData[machineId];
          return {
            machineId,
            totalHours: `${hours}:${minutes.toString().padStart(2, "0")}`, // "6:45" formatında
          };
        });

        setFilteredData(formattedData);
      });
  }, [apiUrl]);

  const showLoading = () => {
    setOpen(true);
    setLoading(true);

    // Simple loading mock. You should add cleanup logic in real world.
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleMachineInfo = (machine) => {
    setSelectedMachineId(machine.key); // Seçilen makinenin ID'sini sakla
    setSelectedMachine(machine); // Mevcut makine bilgilerini yükle
  };

  // Verileri eşleştir ve birleştir
  const mergedData = machines.map((machine, index) => {
    // İlgili departmanı bul
    const department =
      departments.find((dept) => dept._id === machine.department) || {};
    const totalHour =
      filteredData.find((tH) => tH.machineId === machine._id) || {};

    return {
      key: machine._id,
      name: machine.name || "N/A",
      code: machine.code || "Unknown",
      status: machine.status || "inactive",
      machineYear: machine.machineYear || "N/A",
      availability: machine.availability || 0,
      performance: machine.performance || 0,
      quality: machine.quality || 0,
      oee: machine.oee || 0,
      good: machine.good || 0,
      bad: machine.bad || 0,
      total: machine.total || 0,
      departmentName: department.name || "Bilinmiyor",
      // plannedTime: workingHours.plannedTime || "0h",
      utilizedTime: totalHour.totalHours || "0 S",
    };
  });

  const columns = [
    {
      title: "S. No",
      dataIndex: "index",
      key: "index",
      render: (text, record, index) => index + 1,
    },
    {
      title: "M. Adı",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Kod",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Durumu",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={
            status === "active"
              ? "green"
              : status === "inactive"
              ? "red"
              : "yellow"
          }
        >
          {status === "active"
            ? "Aktif"
            : status === "inactive"
            ? "Pasif"
            : "Bakımda"}
        </Tag>
      ),
    },
    {
      title: "M. Yılı",
      dataIndex: "machineYear",
      key: "machineYear",
    },
    {
      title: "Departman",
      dataIndex: "departmentName",
      key: "departmentName",
    },
    {
      title: "Çalışma Saati",
      dataIndex: "utilizedTime",
      key: "utilizedTime",
    },
    {
      title: "Availability Rate",
      dataIndex: "availability",
      key: "availability",
      render: (availability) => (
        <Tag
          color={
            availability >= 70 ? "green" : availability >= 50 ? "orange" : "red"
          }
        >
          {availability}%
        </Tag>
      ),
    },
    {
      title: "Performance",
      dataIndex: "performance",
      key: "performance",
    },
    {
      title: "Quality",
      dataIndex: "quality",
      key: "quality",
    },
    {
      title: "OEE",
      dataIndex: "oee",
      key: "oee",
    },
    {
      title: "Good",
      dataIndex: "good",
      key: "good",
    },
    {
      title: "Bad",
      dataIndex: "bad",
      key: "bad",
    },
    {
      title: "Toplam Ürün",
      dataIndex: "total",
      key: "total",
    },
    {
      title: "İşlem",
      key: "total",
      render: (machine) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => {
              showLoading(); // İlk işlem
              handleMachineInfo(machine); // İkinci işlem
            }}
          >
            <LineChartOutlined />
            Detay
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <DetailDrawer
        open={open}
        loading={loading}
        showLoading={showLoading}
        setOpen={setOpen}
        selectedMachine={selectedMachine}
        selectedMachineId={selectedMachineId}
      />
      <Table
        columns={columns}
        dataSource={mergedData} // Birleştirilmiş datayı tabloya veriyoruz
        pagination={false}
      />
    </div>
  );
};

export default TableComponent;
